import React from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import {wrapGrid} from 'animate-css-grid';
import {StoryboardCell} from '@/views/storyboard/cell';
import ChartContainer from './charts/ChartContainer';
import StoryboardFilters from './filters/StoryboardFilters';
import {withTranslation, WithTranslation} from 'react-i18next';
import i18n from 'i18n';
import {Chart, ChartQuery, ChartSettings, ChartOutput, BarOrientation, BarSortOrder} from 'entities/chart';
import {EchartsChartType} from '@/views/storyboard/utils/chartConstants';
import {resolveDataLabelPosition} from '@/views/storyboard/utils/chartDataLabels';
import {TimelineByDateChartData} from '@/entities/chartData';
import {Icon} from '@/components/icons/Icon';
import Button from '@/components/ui/Button';

interface ChartData extends ChartSettings {
    id: number;
    chartQuery: ChartQuery;
    // Local-only version flag to force chart remounts when configuration changes.
    configVersion?: number;
}

interface StoryboardProps extends WithTranslation {
    allChartsData: ChartData[];
    title: string;
    writeAccess: boolean;
    addChart: () => void;
    onNewChartSave: (chartData: Chart, toPosition?: number) => void;
    moveChart: (dragIndex: number, hoverIndex: number) => void;
    onChartMove: () => void;
    modifyChart: (chartData: Chart, index: number) => void;
    deleteChart: (index: number) => void;
    inGridAnimation: boolean;
    storyIsPublic?: boolean;
    isRearrangeMode: boolean;
}

interface StoryboardState { }

class Storyboard extends React.PureComponent<StoryboardProps, StoryboardState> {
    private gridRef: HTMLDivElement | null = null;

    componentDidMount() {
        if (this.gridRef && this.props.allChartsData.length > 0) {
            wrapGrid(this.gridRef, {easing: 'backOut', stagger: 10, duration: 400});
        }
    }

    render() {
        const {
            allChartsData,
            writeAccess,
            addChart,
            onNewChartSave,
            moveChart,
            onChartMove,
            modifyChart,
            deleteChart,
            inGridAnimation,
            isRearrangeMode,
        } = this.props;

        let charts: React.ReactNode;

        if (Array.isArray(allChartsData) && allChartsData.length) {
            charts = allChartsData.map((obj, idx) => (
                <div
                    className={`storyboard-cell-container width-${obj.width} height-${obj.height}`}
                    key={`${obj.id}-${obj.configVersion ?? 0}`}
                >
                    <StoryboardCell
                        chart={{
                            id: obj.id,
                            title: obj.title,
                            description: obj.description ?? '',
                            width: obj.width,
                            height: obj.height,
                            linearRegression: obj.linearRegression,
                            comparablePeriod: obj.comparablePeriod,
                            ignoreGlobalFilters: false,
                            movingAverageDays: obj.movingAverageDays,
                            movingAverageDaysCenter: obj.movingAverageDaysCenter,
                            scalingFactor: obj.scalingFactor,
                            barOrientation: obj.barOrientation ?? BarOrientation.Vertical,
                            barSortOrder: obj.barSortOrder ?? BarSortOrder.HighToLow,
                            dataLabelPosition: resolveDataLabelPosition(obj.type, obj.dataLabelPosition),
                            type: obj.type,
                        }}
                        chartQuery={obj.chartQuery}
                        index={idx}
                        id={obj.id}
                        writeAccess={writeAccess}
                        isRearrangeMode={isRearrangeMode}
                        moveChart={(dragIndex, hoverIndex) => moveChart(dragIndex, hoverIndex)}
                        onChartMove={() => onChartMove()}
                        onNewChartSave={(chartData, toPosition) => onNewChartSave(chartData, toPosition)}
                        modifyChart={(chartData) => modifyChart(chartData, idx)}
                        deleteChart={(index) => deleteChart(index)}
                    />
                </div>
            ));
        } else if (writeAccess) {
            charts = (
                <div className="storyboard-cell-container width-2 height-2">
                    <button
                        className="storyboard-cell width-2 height-2 example-chart position-relative"
                        onClick={() => addChart()}
                        type="button"
                        data-cy="no-charts-placeholder-cell"
                    >
                        <div className="example-chart-text position-absolute w-100 h-50">
                            <h2>
                                {i18n.t('jt_start_creating_chart.text')}
                            </h2>
                        </div>

                        <div className="storyboard-cell-inner charttype-bar">
                            <div
                                className="storyboard-cell-settings-bar"
                                style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}
                            >
                                <Button
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-settings"
                                    className="border"
                                />
                            </div>

                            <ChartContainer
                                data={Array.from({length: 20}, (x, i) => ({
                                    docCount: i * i * i,
                                    allDocs: 1000,
                                    key: i.toString(),
                                    labelFull: i.toString(),
                                    labelName: 'Date' as const,
                                    labelSearch: [i.toString()],
                                    labelShort: i.toString(),
                                    relativeToAllDocs: (i * i * i) / 1000,
                                })) as TimelineByDateChartData[]}
                                type={EchartsChartType.LineChart}
                                height={2}
                                width={2}
                                linearRegression={false}
                                comparablePeriod
                                movingAverageDays={null}
                                movingAverageDaysCenter
                                scalingFactor=""
                                chartQuery={{
                                    query_string: '*',
                                    output: ChartOutput.TimelineByDate,
                                }}
                                previewOnly={false}
                            />
                        </div>
                    </button>
                </div>
            );
        } else {
            // Empty state for users without write access - provide a proper grid container
            charts = (
                <div className="storyboard-cell-container width-12 height-2">
                    <div className="storyboard-cell width-12 height-2 d-flex align-items-center justify-content-center">
                        <h3 className="text-muted">{i18n.t('jt_no_charts_available.text', 'No charts available')}</h3>
                    </div>
                </div>
            );
        }

        let storyboardClasses = '';
        if (inGridAnimation) {
            storyboardClasses = 'grid-animation';
        }
        if (isRearrangeMode) {
            storyboardClasses += ' rearrange-mode';
        }

        return (
            <>
                <StoryboardFilters />
                {isRearrangeMode && (
                    <div className="alert alert-info d-flex align-items-center mb-3">
                        <Icon name="mdi-information" className="mr-2" />
                        {i18n.t('jt_rearrange_mode_active.text')}
                    </div>
                )}
                <div data-cy="storyboard-charts">
                    <Row className={`storyboard ${storyboardClasses}`}>
                        <Col xs="12">
                            <div
                                className={`storyboard-cells-wrapper grid ${isRearrangeMode ? 'rearrange-active' : ''}`}
                                ref={(el) => {
                                    this.gridRef = el;
                                    return this.gridRef;
                                }}
                            >
                                {charts}
                            </div>
                        </Col>
                    </Row>
                </div>
            </>
        );
    }
}

export default withTranslation()(Storyboard); 